import type {WorkspaceMobileBottomCreateItem} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import {EMOTORAD__LEAD__CREATE__FORM__ID} from "~/domainModels/workspaceSpecific/emotorad_old";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

/**
 * Event form definition ids — same strings as `emotorad.ts` exports with the same names.
 * Inlined here so this module does not import `emotorad.ts` (avoids circular init: e.g. `platform.private` →
 * this file → `emotorad` still loading → "Cannot access … before initialization").
 */
const EMOTORAD__CALL__SCHEDULE__FORM__ID = "37859db2-41cb-54d4-90f8-b6f75b1ae90a" as Uuid;
const EMOTORAD__CALL__LOG__FORM__ID = "56645605-26e8-5b14-a0e8-9284b6344f1c" as Uuid;
const EMOTORAD__WALKIN__SCHEDULE__FORM__ID = "75d608c0-f583-4fe4-9272-6234384286d3" as Uuid;
const EMOTORAD__WALKIN__LOG__FORM__ID = "0d936309-abf5-4c31-b4ef-0e55ffc91570" as Uuid;
const EMOTORAD__TESTRIDE__SCHEDULE__FORM__ID = "11545957-ae52-4923-864c-756f0dda8ff1" as Uuid;
const EMOTORAD__TESTRIDE__LOG__FORM__ID = "b1e803cd-90a0-43bf-adcd-8d89c18324c9" as Uuid;
const EMOTORAD__SALES__SCHEDULE__FORM__ID = "1b71e3a4-4e6e-4c12-94d3-daba10869540" as Uuid;

/** UI-only form id for invite (same role as DHIRAAGU__UI__INVITE_USER__FORM__ID). */
const EMOTORAD__UI__INVITE_USER__FORM__ID = "8e1c2d3f-4a5b-4c6d-8e9f-0a1b2c3d4e5f" as Uuid;

const EMOTORAD__UI__LEAD_CREATE__ID = "65cdc6ce-97a9-4023-bfce-b4dfb0323232" as Uuid;
const EMOTORAD__UI__SCHEDULE_CALL__ID = "a3f8b2c1-4d0e-4f1a-9b2c-3d4e5f6a7b8c" as Uuid;
const EMOTORAD__UI__LOG_CALL__ID = "b4a9c3d2-5e1f-4a2b-8c3d-4e5f6a7b8c9d" as Uuid;
const EMOTORAD__UI__CREATE_SALE__ID = "c5b0d4e3-6f2a-4b3c-9d4e-5f6a7b8c9d0e" as Uuid;
const EMOTORAD__UI__SCHEDULE_TEST_RIDE__ID = "d6c1e5f4-7a3b-4c4d-8e5f-6a7b8c9d0e1f" as Uuid;
const EMOTORAD__UI__LOG_TEST_RIDE__ID = "e7d2f6a5-8b4c-4d5e-8a6a-7b8c9d0e1f2a" as Uuid;
const EMOTORAD__UI__SCHEDULE_WALKIN__ID = "a1f4b8c7-0d6e-4f7a-8c8c-9d0e1f2a3b4c" as Uuid;
const EMOTORAD__UI__LOG_WALKIN__ID = "b2a5c9d8-1e7f-4a8b-ad9d-0e1f2a3b4c5d" as Uuid;
const EMOTORAD__UI__INVITE_USER__ID = "d4c7e1f0-3a9b-4c0d-8f1f-2a3b4c5d6e7f" as Uuid;

/**
 * Default mobile Create sheet for Emotorad when `workspaceUi.configuration.mobileBottomCreateSheet` is omitted.
 * Icons match Dhiraagu (`WORKSPACE_MOBILE_BOTTOM_CREATE_ICON_IDS`).
 */
export const EMOTORAD_DEFAULT_MOBILE_BOTTOM_CREATE_SHEET: readonly WorkspaceMobileBottomCreateItem[] = [
    {
        id: EMOTORAD__UI__LEAD_CREATE__ID,
        formId: EMOTORAD__LEAD__CREATE__FORM__ID,
        formUrl: "emotorad/lead/create",
        iconId: "customers",
        displayName: "Create lead",
    },
    {
        id: EMOTORAD__UI__LOG_CALL__ID,
        formId: EMOTORAD__CALL__LOG__FORM__ID,
        formUrl: "emotorad/call/log",
        iconId: "logCall",
        displayName: "Log a call",
    },
    {
        id: EMOTORAD__UI__SCHEDULE_CALL__ID,
        formId: EMOTORAD__CALL__SCHEDULE__FORM__ID,
        formUrl: "emotorad/call/schedule",
        iconId: "scheduleCall",
        displayName: "Schedule a call",
    },
    {
        id: EMOTORAD__UI__LOG_WALKIN__ID,
        formId: EMOTORAD__WALKIN__LOG__FORM__ID,
        formUrl: "emotorad/walkin/log",
        iconId: "logCall",
        displayName: "Log walk-in",
    },
    {
        id: EMOTORAD__UI__SCHEDULE_WALKIN__ID,
        formId: EMOTORAD__WALKIN__SCHEDULE__FORM__ID,
        formUrl: "emotorad/walkin/schedule",
        iconId: "scheduleCall",
        displayName: "Schedule walk-in",
    },
    {
        id: EMOTORAD__UI__LOG_TEST_RIDE__ID,
        formId: EMOTORAD__TESTRIDE__LOG__FORM__ID,
        formUrl: "emotorad/testride/log",
        iconId: "logCall",
        displayName: "Log test ride",
    },
    {
        id: EMOTORAD__UI__SCHEDULE_TEST_RIDE__ID,
        formId: EMOTORAD__TESTRIDE__SCHEDULE__FORM__ID,
        formUrl: "emotorad/testride/schedule",
        iconId: "scheduleCall",
        displayName: "Schedule test ride",
    },
    {
        id: EMOTORAD__UI__CREATE_SALE__ID,
        formId: EMOTORAD__SALES__SCHEDULE__FORM__ID,
        formUrl: "emotorad/sale/create",
        iconId: "sales",
        displayName: "Create sale",
    },
    {
        id: EMOTORAD__UI__INVITE_USER__ID,
        formId: EMOTORAD__UI__INVITE_USER__FORM__ID,
        formUrl: "user-invitation",
        iconId: "inviteUser",
        displayName: "Invite user",
    },
];
